import { useEffect, useState } from "react";
import { useDataProvider } from "react-admin";
import { DateTime } from "luxon";
import { socioPhotoUrl } from "../../util/socioPhoto";

// ------------------------------------------------------------------
// Hook central de dados do dashboard.
// Usa apenas endpoints existentes da API (contagens via getList com
// perPage 1 + filtros que o backend já suporta). Nenhum backend novo.
// ------------------------------------------------------------------

export type MemberStatusCounts = {
  active: number;
  canceled: number;
  deceased: number;
  noContact: number;
  licensed: number;
  pending: number;
  unknown: number;
};

export type RecentMember = {
  id: string;
  fullName: string;
  createdAt: string;
  accountStatus: string;
  photoUrl?: string;
};

export type MonthlySeries = { labels: string[]; values: number[] };
export type NamedSeries = { labels: string[]; values: number[] };

// Fase 2 dos KPIs (endpoint /transactions/stats/overview): receita em R$
export type RevenueOverview = {
  monthly: MonthlySeries;
  semester: {
    currentTotal: number;
    currentLabel: string;
    variationPct: number | null;
  };
  byCategory: NamedSeries;
};

const MONTHS_IN_CHART = 12;

export function useDashboardData() {
  const dataProvider = useDataProvider();

  const [statusCounts, setStatusCounts] = useState<MemberStatusCounts | null>(
    null
  );
  const [adimplentCount, setAdimplentCount] = useState<number | null>(null);
  const [incomeCount, setIncomeCount] = useState<number | null>(null);
  const [orphanedCount, setOrphanedCount] = useState<number | null>(null);
  const [birthdayCount, setBirthdayCount] = useState<number | null>(null);
  const [newThisMonth, setNewThisMonth] = useState<number | null>(null);
  const [newPrevMonth, setNewPrevMonth] = useState<number | null>(null);
  const [monthly, setMonthly] = useState<MonthlySeries | null>(null);
  const [cancellations, setCancellations] = useState<MonthlySeries | null>(
    null
  );
  const [categories, setCategories] = useState<NamedSeries | null>(null);
  const [recent, setRecent] = useState<RecentMember[] | null>(null);
  const [revenue, setRevenue] = useState<RevenueOverview | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Contagem barata: perPage 1, interessa só o total
    const count = (resource: string, filter: any = {}): Promise<number> =>
      dataProvider
        .getList(resource, {
          pagination: { page: 1, perPage: 1 },
          sort: { field: "id", order: "ASC" },
          filter,
        })
        .then((r: any) => r.total ?? 0)
        .catch(() => 0);

    // O backend espera as datas aninhadas em dateFieldValue (formato do
    // DateFilterSidebar): { dateField, dateFieldValue: { dateStart, dateEnd } }.
    // O campo é genérico no servidor ([dateField]: gte/lte) — serve tanto
    // para createdAt (novos sócios) quanto para cancellationDate
    const countMembersBetween = (
      start: DateTime,
      end: DateTime,
      dateField = "createdAt"
    ) =>
      count("users/crud", {
        dateField,
        dateFieldValue: {
          dateStart: start.toFormat("yyyy-MM-dd"),
          dateEnd: end.toFormat("yyyy-MM-dd"),
        },
      });

    const now = DateTime.now();

    // --- Situação dos sócios (donut + total) ---
    (async () => {
      const [
        active,
        canceled,
        deceased,
        noContact,
        licensed,
        pending,
        unknown,
      ] = await Promise.all(
        [
          "ACTIVE",
          "CANCELED",
          "DECEASED",
          "NO_CONTACT",
          "LICENSED",
          "PENDING",
          "UNKNOWN",
        ].map((status) => count("users/crud", { accountStatus: [status] }))
      );
      if (!cancelled)
        setStatusCounts({
          active,
          canceled,
          deceased,
          noContact,
          licensed,
          pending,
          unknown,
        });
    })();

    // --- Indicadores simples ---
    // Adimplentes ATIVOS: a coluna userEnabled é recalculada em background
    // pelo servidor (cron, ~3 min de defasagem). O recorte precisa casar com
    // o denominador do card (sócios ativos): sem o accountStatus, entravam
    // 4,6 mil adimplentes de outras situações (licenciados, pendentes…) e
    // a adimplência saía 67% em vez de ~41%. Mesmo par de filtros que a
    // lista de Sócios usa (Situação = Ativo + Adimplência = Adimplente).
    count("users/crud", {
      accountStatus: ["ACTIVE"],
      userEnabled: "true",
    }).then((v) => !cancelled && setAdimplentCount(v));
    count("transactions/crud", { type: "INCOME" }).then(
      (v) => !cancelled && setIncomeCount(v)
    );
    count("orphaned-users/crud").then(
      (v) => !cancelled && setOrphanedCount(v)
    );
    count("users/crud", { birthMonth: String(now.month) }).then(
      (v) => !cancelled && setBirthdayCount(v)
    );
    // As contagens por forma de pagamento (Boleto/Iugu/Pix/Cartão/
    // Isenções), Assinaturas e Despesas saíram daqui junto com os
    // cards: os recortes já vivem nos KPIs da tela de Transações —
    // eram consultas duplicadas a cada abertura do Dashboard

    // --- Novos sócios: mês atual × mês anterior ---
    countMembersBetween(now.startOf("month"), now).then(
      (v) => !cancelled && setNewThisMonth(v)
    );
    const prev = now.minus({ months: 1 });
    countMembersBetween(prev.startOf("month"), prev.endOf("month")).then(
      (v) => !cancelled && setNewPrevMonth(v)
    );

    // --- Série: novos sócios por mês (últimos 12 meses) ---
    (async () => {
      const months = Array.from({ length: MONTHS_IN_CHART }, (_, i) =>
        now.minus({ months: MONTHS_IN_CHART - 1 - i })
      );
      const values = await Promise.all(
        months.map((m) =>
          countMembersBetween(m.startOf("month"), m.endOf("month"))
        )
      );
      if (!cancelled)
        setMonthly({
          labels: months.map((m) =>
            m.setLocale("pt-BR").toFormat("LLL/yy")
          ),
          values,
        });
    })();

    // --- Série: cancelamentos por mês (últimos 12 meses) ---
    // Mesma mecânica da série de novos sócios, trocando o campo de
    // data para cancellationDate (a data de cancelamento gravada no
    // cadastro) — o filtro de datas do servidor é genérico, nenhum
    // backend novo. Conta pela data registrada, independente da
    // situação atual (um reativado continua contado no mês em que o
    // cancelamento aconteceu).
    (async () => {
      const months = Array.from({ length: MONTHS_IN_CHART }, (_, i) =>
        now.minus({ months: MONTHS_IN_CHART - 1 - i })
      );
      const values = await Promise.all(
        months.map((m) =>
          countMembersBetween(
            m.startOf("month"),
            m.endOf("month"),
            "cancellationDate"
          )
        )
      );
      if (!cancelled)
        setCancellations({
          labels: months.map((m) =>
            m.setLocale("pt-BR").toFormat("LLL/yy")
          ),
          values,
        });
    })();

    // --- Sócios por categoria ---
    (async () => {
      try {
        const cats = await dataProvider.getList("user-categories/crud", {
          pagination: { page: 1, perPage: 50 },
          sort: { field: "id", order: "ASC" },
          filter: {},
        });
        const values = await Promise.all(
          cats.data.map((cat: any) =>
            count("users/crud", { userCategoryId: [cat.id] })
          )
        );
        if (!cancelled)
          setCategories({
            labels: cats.data.map((cat: any) => cat.name ?? cat.id),
            values,
          });
      } catch {
        if (!cancelled) setCategories({ labels: [], values: [] });
      }
    })();

    // --- Receita em R$ (fase 2 dos KPIs) ---
    // Endpoint de agregação read-only do servidor; fetch direto (o
    // dataProvider só fala o dialeto de listas do react-admin)
    (async () => {
      try {
        const auth = localStorage.getItem("auth");
        const token = auth ? JSON.parse(auth)?.access_token : "";
        const resp = await fetch(
          `${process.env.REACT_APP_API_PATH}/transactions/stats/overview`,
          { headers: { Authorization: "Bearer " + token } }
        );
        if (!resp.ok) throw new Error(String(resp.status));
        const data = await resp.json();
        // Esqueleto dos 12 meses preenchido com o que o servidor mandou:
        // o GROUP BY só devolve meses COM transação — sem isso, um mês
        // zerado (ex.: o atual recém-começado) sumiria do gráfico e
        // desalinharia os rótulos
        const byYm = new Map<string, number>(
          (data.monthly ?? []).map((m: any) => [m.ym, m.total])
        );
        const months = Array.from({ length: MONTHS_IN_CHART }, (_, i) =>
          now.minus({ months: MONTHS_IN_CHART - 1 - i })
        );
        const monthlySeries: MonthlySeries = {
          labels: months.map((m) =>
            m.setLocale("pt-BR").toFormat("LLL/yy")
          ),
          values: months.map(
            (m) => byYm.get(m.toFormat("yyyy-MM")) ?? 0
          ),
        };
        const semKey = String(data.semester?.current?.sem ?? "");
        const [semYear, semHalf] = semKey.split(".");
        if (!cancelled)
          setRevenue({
            monthly: monthlySeries,
            semester: {
              currentTotal: data.semester?.current?.total ?? 0,
              // "(em andamento)": o semestre corrente é parcial — sem o
              // aviso, a comparação com o anterior completo assusta
              currentLabel: semHalf
                ? `${semHalf}º semestre ${semYear} (em andamento)`
                : "",
              variationPct: data.semester?.variationPct ?? null,
            },
            byCategory: {
              labels: (data.byCategory ?? []).map((c: any) => c.name),
              values: (data.byCategory ?? []).map((c: any) => c.total),
            },
          });
      } catch {
        // Sem o endpoint (ex.: backend antigo), o Dashboard segue sem a
        // seção de R$ — os demais cartões não são afetados
        if (!cancelled) setRevenue(null);
      }
    })();

    // --- Últimos cadastros ---
    dataProvider
      .getList("users/crud", {
        pagination: { page: 1, perPage: 5 },
        sort: { field: "createdAt", order: "DESC" },
        filter: {},
      })
      .then(
        (r: any) =>
          !cancelled &&
          setRecent(
            r.data.map((u: any) => ({
              id: u.id,
              fullName: u.fullName ?? "(sem nome)",
              createdAt: u.createdAt,
              accountStatus: u.accountStatus ?? "UNKNOWN",
              photoUrl: socioPhotoUrl(u),
            }))
          )
      )
      .catch(() => !cancelled && setRecent([]));

    return () => {
      cancelled = true;
    };
  }, [dataProvider]);

  const totalMembers = statusCounts
    ? Object.values(statusCounts).reduce((a, b) => a + b, 0)
    : null;

  return {
    statusCounts,
    totalMembers,
    adimplentCount,
    incomeCount,
    orphanedCount,
    birthdayCount,
    newThisMonth,
    newPrevMonth,
    monthly,
    cancellations,
    categories,
    recent,
    revenue,
  };
}
